/**
 * ImportTypeChip — Tier 0 module-local chip for import types.
 *
 * Per `.design-incoming/admin-20260522/IMPORTS_SPEC.md §1`. Four
 * import types per `DATA_MODEL.md §4`:
 *
 *   - `properties`        → "Properties"
 *   - `reservations`      → "Reservations"
 *   - `owners`            → "Owners"
 *   - `owner_statements`  → "Owner statements"
 *
 * Visual style mirrors `TypeChip` (ISA/MSA) + `StaffRoleChip` so
 * the dense queue table looks coherent across surfaces. Renders
 * as a neutral `<span>` Tier-0 badge until kit `<Badge>` (PR #143)
 * ships — same precedent SCRUM-118 + SCRUM-123 established.
 *
 * Per spec §1: "Type, rendered as a chip" in the desktop queue
 * table. The mobile card-row variant uses plain text after the
 * status dot to avoid crowding; that's a surface decision
 * (made in the page, not here).
 */

import * as React from 'react';

export type ImportType =
  | 'properties'
  | 'reservations'
  | 'owners'
  | 'owner_statements';

const LABELS: Record<ImportType, string> = {
  properties: 'Properties',
  reservations: 'Reservations',
  owners: 'Owners',
  owner_statements: 'Owner statements',
};

export function importTypeLabel(type: ImportType): string {
  return LABELS[type];
}

export interface ImportTypeChipProps {
  readonly type: ImportType;
  readonly size?: 'sm' | 'md';
}

export function ImportTypeChip({
  type,
  size = 'md',
}: ImportTypeChipProps): React.ReactElement {
  return (
    <span
      data-testid={`import-type-chip-${type}`}
      className="inline-flex items-center font-medium"
      style={{
        padding: size === 'sm' ? '2px 8px' : '3px 10px',
        borderRadius: 'var(--radius-full)',
        background: 'var(--bg-sunken)',
        color: 'var(--fg-secondary)',
        border: '1px solid var(--border-default)',
        fontSize: size === 'sm' ? '11px' : '12px',
        whiteSpace: 'nowrap',
      }}
    >
      {LABELS[type]}
    </span>
  );
}
